import * as React from 'react';
import { CalendarDays } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/utils';

export interface DateRange {
  from?: Date | undefined;
  to?: Date | undefined;
}

interface DateRangePickerProps {
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onApply?: () => void;
  mode?: 'default' | 'weekly';
  disableClear?: boolean;
  textColor?: string;
}

// Utilities
const startOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const endOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

const formatDate = (date: Date) => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const PRESET_RANGES = [
  {
    label: 'Today',
    value: 'today',
    getRange: () => ({
      from: startOfDay(new Date()),
      to: endOfDay(new Date()),
    }),
  },
  {
    label: 'Yesterday',
    value: 'yesterday',
    getRange: () => {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    },
  },
  {
    label: 'Last 7 days',
    value: 'last-7',
    getRange: () => {
      const from = new Date();
      from.setDate(from.getDate() - 6);
      return { from: startOfDay(from), to: endOfDay(new Date()) };
    },
  },
  {
    label: 'Last 30 days',
    value: 'last-30',
    getRange: () => {
      const from = new Date();
      from.setDate(from.getDate() - 29);
      return { from: startOfDay(from), to: endOfDay(new Date()) };
    },
  },
  {
    label: 'Last 90 days',
    value: 'last-90',
    getRange: () => {
      const from = new Date();
      from.setDate(from.getDate() - 89);
      return { from: startOfDay(from), to: endOfDay(new Date()) };
    },
  },
  {
    label: 'This month',
    value: 'this-month',
    getRange: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: startOfDay(from), to: endOfDay(to) };
    },
  },
];

export const getWeekRange = (date: Date) => {
  const d = new Date(date);

  // JS: Sunday = 0, Monday = 1
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return { from: monday, to: sunday };
};

export function DateRangePicker({
  value,
  onChange,
  placeholder = 'mm / dd / yy',
  className = 'w-fit',
  disabled = false,
  onApply,
  mode = 'default',
  disableClear = false,
  textColor = 'text-textcolor',
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [fromInput, setFromInput] = React.useState<string>('');
  const [toInput, setToInput] = React.useState<string>('');
  const popupRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (popupRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;

      const radixPortal = document.querySelector('[data-radix-popper-content-wrapper]');
      if (radixPortal && radixPortal.contains(target)) return;
      setIsOpen(false);
      onApply?.();
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Sync with parent value
  const formatForInput = (date: Date) => date.toLocaleDateString('en-CA');

  React.useEffect(() => {
    setFromInput(value?.from ? formatForInput(value.from) : '');
    setToInput(value?.to ? formatForInput(value.to) : '');
  }, [value]);

  const handlePresetChange = (presetValue: string) => {
    const preset = PRESET_RANGES.find((p) => p.value === presetValue);
    if (preset && onChange) {
      onChange(preset.getRange());
    }
  };

  const handleFromChange = (v: string) => {
    setFromInput(v);
    if (!v) {
      onChange?.({ from: undefined, to: value?.to });
      return;
    }

    const selected = new Date(v);

    if (mode === 'weekly') {
      onChange?.(getWeekRange(selected));
    } else {
      selected.setHours(0, 0, 0, 0);
      onChange?.({ from: selected, to: value?.to });
    }
  };

  const handleToChange = (v: string) => {
    setToInput(v);
    if (!v) {
      onChange?.({ from: value?.from, to: undefined });
      return;
    }

    const selected = new Date(v);

    if (mode === 'weekly') {
      onChange?.(getWeekRange(selected));
    } else {
      selected.setHours(23, 59, 59, 999);
      onChange?.({ from: value?.from, to: selected });
    }
  };

  const clearRange = () => {
    setFromInput('');
    setToInput('');
    onChange?.({ from: undefined, to: undefined });
  };

  const formatDisplay = (d?: Date) => (d ? formatDate(d) : placeholder);

  const hasError = value?.from && value?.to && value.from.getTime() > value.to.getTime();

  return (
    <div className={cn('relative w-full', className)}>
      {/* MAIN TWO INPUTS */}
      <div
        ref={triggerRef}
        onClick={() => !disabled && setIsOpen(true)}
        className={cn(
          `
    flex items-center justify-between w-full
    rounded-xl border
    px-3 py-2 sm:px-4 sm:py-2
    cursor-pointer
    gap-2
    bg-white/20 backdrop-blur-xl border-white/20
    `,
          hasError ? 'border-red-500' : 'border-green',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {/* LEFT SIDE: CALENDAR ICON + TEXT */}
        <div
          className={cn(
            `
      flex items-center
      gap-2 sm:gap-3
      text-xs sm:text-sm font-medium
      leading-none
      flex-nowrap overflow-hidden
      `,
            textColor
          )}
        >
          <CalendarDays
            className={cn(
              `
        h-4 w-4 sm:h-6 sm:w-6
        border-green rounded-sm p-0.5
        shrink-0
        `,
              textColor
            )}
          />

          {/* FROM DATE */}
          <span
            className={`text-[10px] sm:text-xs whitespace-nowrap ${
              !value?.from ? 'text-textcolor' : ''
            }`}
          >
            {value?.from ? formatDisplay(value.from) : placeholder}
          </span>

          {/* TO LABEL */}
          <span className="text-textcolor text-[10px] sm:text-xs mx-1 sm:mx-2">To</span>

          {/* TO DATE */}
          <span
            className={`text-[10px] sm:text-xs whitespace-nowrap ${
              !value?.to ? 'text-textcolor' : ''
            }`}
          >
            {value?.to ? formatDisplay(value.to) : placeholder}
          </span>
        </div>
      </div>

      {/* ERROR */}
      {hasError && <p className="text-sm text-red-500 mt-1">Start date cannot be after end date</p>}

      {/* POPUP */}
      {isOpen && (
        <Card
          ref={popupRef}
          className="absolute right-0 top-full z-50 mt-1 w-[95vw] max-w-[340px] sm:w-[340px]"
        >
          <CardContent className="space-y-4 p-3">
            {/* Quick Select */}
            {mode === 'default' && (
              <div>
                <Label className="text-xs sm:text-sm">Quick Select</Label>

                <Select onValueChange={handlePresetChange}>
                  <SelectTrigger className="mt-1 w-full text-xs sm:text-sm">
                    <SelectValue placeholder="Choose preset" />
                  </SelectTrigger>

                  <SelectContent>
                    {PRESET_RANGES.map((p) => (
                      <SelectItem key={p.value} value={p.value} className="text-xs sm:text-sm">
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Custom Range */}
            <div className="grid gap-1 grid-cols-2 sm:gap-2">
              {/* From */}
              <div>
                <Label className="text-xs sm:text-sm">From</Label>

                <Input
                  type="date"
                  value={fromInput}
                  onChange={(e) => handleFromChange(e.target.value)}
                  className="text-xs sm:text-sm"
                />
              </div>

              {/* To */}
              <div>
                <Label className="text-xs sm:text-sm">To</Label>

                <Input
                  type="date"
                  value={toInput}
                  onChange={(e) => handleToChange(e.target.value)}
                  style={{ direction: 'rtl' }}
                  className="text-left text-xs sm:text-sm"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 border-t pt-3">
              {!disableClear && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={clearRange}
                  className="text-xs sm:text-sm"
                >
                  Clear
                </Button>
              )}

              <Button
                size="sm"
                variant="green"
                className="text-xs sm:text-sm"
                onClick={() => {
                  onApply?.();
                  setIsOpen(false);
                }}
              >
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
